<div class="space-y-6 pb-10">
  <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
    <div>
      <h2 class="text-2xl font-bold tracking-tight">Refund Requests</h2>
      <p class="text-base-content/60">
        Approval does not cancel an order. Gateway refunds require provider confirmation; manual external refunds are recorded separately.
      </p>
    </div>
    <div class="flex gap-2">
      <button class="btn btn-primary" (click)="openCreateModal()">
        <i class="fa-solid fa-plus"></i>
        Create request
      </button>
      <button class="btn btn-outline" (click)="loadRequests()">
        <i class="fa-solid fa-rotate"></i>
        Refresh
      </button>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Total requested</span>
        <span class="text-2xl font-bold">{{ totals().requested | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Pending</span>
        <span class="text-2xl font-bold text-warning">{{ totals().pending | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Approved</span>
        <span class="text-2xl font-bold text-info">{{ totals().approved | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Refunded</span>
        <span class="text-2xl font-bold text-success">{{ totals().refunded | indianCompact:true }}</span>
      </div>
    </div>
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body py-4">
        <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Rejected</span>
        <span class="text-2xl font-bold text-error">{{ totals().rejected | indianCompact:true }}</span>
      </div>
    </div>
  </div>

  <div class="flex flex-wrap items-center gap-3">
    <div class="tabs tabs-boxed w-fit bg-base-200/50 p-1">
      @for (tab of statusTabs; track tab) {
        <button class="tab capitalize" [class.tab-active]="activeFilter === tab" (click)="setFilter(tab)">
          {{ tab }}
        </button>
      }
    </div>

    <select class="select select-bordered select-sm" [(ngModel)]="selectedStore" (ngModelChange)="page = 1; loadRequests()" [disabled]="!auth.isSuperAdmin()">
      @if(auth.isSuperAdmin()){<option [ngValue]="0">All stores</option>}
      @for (store of stores(); track store.id) {
        <option [ngValue]="store.id">{{ store.name }}</option>
      }
    </select>

    <input
      type="text"
      class="input input-bordered input-sm min-w-[200px]"
      placeholder="Search customer / order"
      [(ngModel)]="customerSearch"
      (keyup.enter)="page = 1; loadRequests()"
    />

    <input type="date" class="input input-bordered input-sm" [(ngModel)]="fromDate" (ngModelChange)="page = 1; loadRequests()">
    <input type="date" class="input input-bordered input-sm" [(ngModel)]="toDate" (ngModelChange)="page = 1; loadRequests()">
  </div>

  <div class="card overflow-hidden border border-base-200 bg-base-100 shadow-sm">
    <div class="overflow-x-auto">
      <table class="table table-md w-full">
        <thead class="bg-base-200/50">
          <tr>
            <th>Request ID</th>
            <th>Order</th>
            <th>Customer</th>
            <th>Store</th>
            <th>Paid / Requested / Approved / Processed</th>
            <th>Requested via</th>
            <th>Request date</th>
            <th>Status</th>
            <th>Remarks</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (request of requests(); track request.id) {
            <tr class="hover:bg-base-200/30">
              <td class="font-mono text-sm">#{{ request.id }}</td>
              <td>
                <div class="font-bold">#{{ request.order_number || request.order_id }}</div>
                <div class="text-xs opacity-50">{{ request.order_status }} · {{ request.order_payment_status }}</div>
              </td>
              <td>
                <div class="font-bold">{{ request.customer?.name || 'Customer' }}</div>
                <div class="text-xs font-mono opacity-60">{{ request.customer?.phone || '—' }}</div>
              </td>
              <td>{{ request.store?.name || '—' }}</td>
              <td class="font-semibold text-primary">
                <div>{{ request.paid_amount ?? 'Not linked' }} / {{ request.amount }} / {{ request.approved_amount ?? '—' }} / {{ request.processed_amount ?? '—' }}</div>
                <div class="text-xs">{{ request.payment_method }} · {{ request.manual_external_refund ? 'Manual external' : request.provider }}</div>
              </td>
              <td class="capitalize">{{ request.requested_via }}</td>
              <td>{{ request.created_at | istDate:'medium' }}</td>
              <td>
                <span class="badge badge-sm capitalize" [ngClass]="statusClass(request.status)">
                  {{ request.status }}
                </span>
                <div class="text-xs">Provider: {{ request.provider_status || 'Not confirmed' }}</div>
                <div class="text-xs">{{ request.last_attempt_at | istDate:'short' }}</div>
                <div class="text-xs text-error">{{ request.failure_message }}</div>
              </td>
              <td class="max-w-[180px] truncate text-xs opacity-70" [title]="request.remarks || request.reason">
                {{ request.remarks || request.reason }}
              </td>
              <td class="text-right">
                <div class="flex justify-end gap-2">
                  @if (request.status === 'pending') {
                    <button class="btn btn-xs btn-primary" (click)="approveRequest(request)" [disabled]="isSubmitting()">Approve</button>
                    <button class="btn btn-xs btn-outline btn-error" (click)="openReject(request)">Reject</button>
                  }
                  @if (request.status === 'approved' || request.status === 'failed') {
                    <button class="btn btn-xs btn-primary" (click)="processRefund(request)" [disabled]="isSubmitting()">{{ request.status === 'failed' ? 'Retry gateway' : 'Process gateway' }}</button>
                    @if (canMarkRefunded(request)) {
                      <button class="btn btn-xs btn-outline" (click)="openMarkRefunded(request)" [disabled]="isSubmitting()">Manual external refund</button>
                    } @else {
                      <span class="text-[10px] font-bold uppercase opacity-45">Not paid</span>
                    }
                    <button class="btn btn-xs btn-outline btn-error" (click)="openReject(request)">Reject</button>
                  }
                  @if (request.status === 'processing') {
                    <button class="btn btn-xs btn-primary" (click)="processRefund(request)" [disabled]="isSubmitting()">Check gateway status</button>
                  }
                  <button class="btn btn-xs btn-outline" (click)="showHistory(request)">History</button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="10" class="py-12 text-center text-base-content/60">No refund requests found.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-between border-t border-base-200 bg-base-200/30 p-4">
      <div class="text-sm text-base-content/70">
        Showing {{ totalItems === 0 ? 0 : (page - 1) * pageSize + 1 }} to
        {{ Math.min(page * pageSize, totalItems) }} of {{ totalItems }}
      </div>
      <div class="join">
        <button class="join-item btn btn-sm" [disabled]="page === 1" (click)="onPageChange(page - 1)">«</button>
        <button class="join-item btn btn-sm pointer-events-none">Page {{ page }} / {{ totalPages || 1 }}</button>
        <button class="join-item btn btn-sm" [disabled]="page >= totalPages" (click)="onPageChange(page + 1)">»</button>
      </div>
    </div>
  </div>
</div>

@if (showCreateModal()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
    <div class="w-full max-w-lg rounded-2xl border border-base-300 bg-base-100 p-6 shadow-2xl">
      <h3 class="text-lg font-bold">Create refund request</h3>
      <p class="mt-1 text-sm opacity-60">Use after support verifies the customer request.</p>

      <div class="mt-4 space-y-4">
        <label class="form-control w-full">
          <div class="label"><span class="label-text font-semibold">Order number</span></div>
          <input class="input input-bordered w-full font-mono uppercase" [(ngModel)]="createForm.orderRef" placeholder="e.g. SUP260802547273">
          <div class="label">
            <span class="label-text-alt opacity-60">Use the order number from the orders table (order_number)</span>
          </div>
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text font-semibold">Amount (optional)</span></div>
          <input class="input input-bordered w-full" [(ngModel)]="createForm.amount" placeholder="Defaults to order payable amount">
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text font-semibold">Requested via</span></div>
          <select class="select select-bordered w-full capitalize" [(ngModel)]="createForm.requestedVia">
            @for (via of requestedViaOptions; track via) {
              <option [value]="via">{{ via }}</option>
            }
          </select>
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text font-semibold">Reason</span></div>
          <textarea class="textarea textarea-bordered w-full" rows="3" [(ngModel)]="createForm.reason"></textarea>
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text font-semibold">Internal remarks (optional)</span></div>
          <textarea class="textarea textarea-bordered w-full" rows="2" [(ngModel)]="createForm.remarks"></textarea>
        </label>
      </div>

      <div class="mt-6 flex justify-end gap-2">
        <button class="btn btn-ghost" (click)="closeCreateModal()" [disabled]="isSubmitting()">Cancel</button>
        <button class="btn btn-primary" (click)="submitCreate()" [disabled]="isSubmitting()">Create</button>
      </div>
    </div>
  </div>
}

@if (actionRequest(); as request) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
    <div class="w-full max-w-lg rounded-2xl border border-base-300 bg-base-100 p-6 shadow-2xl">
      <h3 class="text-lg font-bold">
        @if (actionType() === 'reject') { Reject refund #{{ request.id }} }
        @else { Record manual external refund #{{ request.id }} }
      </h3>

      @if (actionType() === 'refunded') {
        <p class="alert alert-warning">Record only money already returned outside Supidoo. This is not gateway confirmation. Amount: {{ request.approved_amount }}. A reason and external reference are required.</p>
        <label class="form-control mt-4 w-full">
          <div class="label"><span class="label-text font-semibold">Transaction reference</span></div>
          <input class="input input-bordered w-full" [(ngModel)]="actionTransactionRef" placeholder="Cashfree / UTR / UPI ref">
        </label>
      }

      <label class="form-control mt-4 w-full">
        <div class="label">
          <span class="label-text font-semibold">
            @if (actionType() === 'reject') { Rejection reason }
            @else { Notes (optional) }
          </span>
        </div>
        <textarea class="textarea textarea-bordered w-full" rows="3" [(ngModel)]="actionRemarks"></textarea>
      </label>

      <div class="mt-6 flex justify-end gap-2">
        <button class="btn btn-ghost" (click)="closeActionModal()" [disabled]="isSubmitting()">Cancel</button>
        <button
          class="btn"
          [class.btn-error]="actionType() === 'reject'"
          [class.btn-success]="actionType() === 'refunded'"
          (click)="submitAction()"
          [disabled]="isSubmitting()"
        >
          @if (actionType() === 'reject') { Reject }
          @else { Confirm refund }
        </button>
      </div>
    </div>
  </div>
}

@if (historyData()) {
  <div class="fixed inset-0 z-50 overflow-auto bg-black/40 p-6">
    <div class="mx-auto max-w-4xl rounded-xl bg-base-100 p-6">
      <button class="btn" (click)="historyData.set(null)">Close history</button>
      <pre class="whitespace-pre-wrap text-xs">{{ historyData() | json }}</pre>
    </div>
  </div>
}
